import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Shield, Plus, CheckCircle2, AlertTriangle, X, Edit3, Calendar, Phone } from 'lucide-react';
import { theme, cardStyle, btnStyle, inputStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  INSURANCE VERIFY — verification queue + policy CRUD
// ═══════════════════════════════════════════════════════════════════

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const STATUS = {
  unverified: { color: '#94a3b8', label: 'UNVERIFIED' },
  verified:   { color: '#16a34a', label: 'VERIFIED' },
  declined:   { color: '#dc2626', label: 'DECLINED' },
  expired:    { color: '#dc2626', label: 'EXPIRED' },
};

const InsuranceVerifyPage = ({ API_BASE, currentUser }) => {
  const [queue, setQueue] = useState([]);
  const [allPolicies, setAllPolicies] = useState([]);
  const [tab, setTab] = useState('queue');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const showMsg = (t, x) => { setMsg({ type: t, text: x }); setTimeout(() => setMsg({ type: '', text: '' }), 3500); };

  const load = useCallback(async () => {
    try {
      const [q, p] = await Promise.all([
        axios.get(`${API_BASE}/insurance-policies/verify-queue`),
        axios.get(`${API_BASE}/insurance-policies?active=true`),
      ]);
      setQueue(q.data || []); setAllPolicies(p.data || []);
    } catch {}
    setLoading(false);
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const list = tab === 'queue' ? queue : allPolicies;

  const verify = async (id, status, notes) => {
    try {
      await axios.post(`${API_BASE}/insurance-policies/${id}/verify`, { status, notes, by: currentUser?.fullName || 'admin' });
      showMsg('success', '✓ Saved'); load(); setVerifying(null);
    } catch (e) { showMsg('error', e.message); }
  };

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 14, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}><Shield size={22} style={{ verticalAlign: -4, marginRight: 6 }} /> Insurance Verification</h2>
            <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>
              <strong>{queue.length}</strong> need verification · <strong>{allPolicies.length}</strong> total active policies
            </p>
          </div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <button onClick={async () => {
              if (!window.confirm(`Auto-verify ${queue.length} pending policies via TPA provider?`)) return;
              try {
                const r = await axios.post(`${API_BASE}/insurance-policies/auto-verify-queue`);
                showMsg('success', `✓ ${r.data.verified} verified · ${r.data.declined} declined · ${r.data.unchanged} unchanged`);
                load();
              } catch (e) { showMsg('error', e.response?.data?.error || e.message); }
            }} style={{ ...btnStyle, marginTop: 0, background: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' }}>
              🤖 Auto-Verify Queue
            </button>
            <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ ...btnStyle, marginTop: 0, background: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' }}>
              <Plus size={14} style={{ marginRight: 4 }} /> Add Policy
            </button>
          </div>
        </div>
      </div>

      {msg.text && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8,
          background: msg.type === 'error' ? '#fee' : '#efe',
          color: msg.type === 'error' ? theme.danger : theme.success,
          border: `1px solid ${msg.type === 'error' ? theme.danger : theme.success}44`, fontWeight: 600, fontSize: 13.5 }}>{msg.text}</div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[['queue', `Verify Queue (${queue.length})`], ['all', `All Policies (${allPolicies.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '8px 16px', borderRadius: 8,
            border: `1.5px solid ${tab === k ? theme.primary : theme.border}`,
            background: tab === k ? theme.primary + '15' : 'white',
            color: tab === k ? theme.primary : theme.textMuted,
            fontWeight: 700, fontSize: 13, cursor: 'pointer',
          }}>{l}</button>
        ))}
      </div>

      {loading ? <div style={{ ...cardStyle, padding: 40, textAlign: 'center' }}>Loading…</div>
        : list.length === 0 ? (
          <div style={{ ...cardStyle, padding: 50, textAlign: 'center' }}>
            <Shield size={40} color={theme.textSubtle} style={{ marginBottom: 8, opacity: 0.5 }} />
            <p style={{ color: theme.textMuted, fontWeight: 600 }}>{tab === 'queue' ? 'All verified ✓' : 'No policies yet'}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {list.map(p => {
              const s = STATUS[p.verificationStatus] || STATUS.unverified;
              const expired = p.validTo && new Date(p.validTo) < new Date();
              const utilPct = p.sumInsured > 0 ? Math.round((p.sumUtilized / p.sumInsured) * 100) : 0;
              return (
                <div key={p._id} style={{ ...cardStyle, padding: 14, borderLeft: `4px solid ${expired ? '#dc2626' : s.color}` }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: 'white', background: s.color, padding: '2px 7px', borderRadius: 4 }}>{s.label}</span>
                        {expired && <span style={{ fontSize: 10, fontWeight: 700, color: '#dc2626' }}>⚠ EXPIRED</span>}
                        {p.upcomingAppointment && <span style={{ fontSize: 10, color: theme.primaryDark, background: theme.primary + '18', padding: '2px 7px', borderRadius: 4 }}>📅 {fmt(p.upcomingAppointment.date)} {p.upcomingAppointment.time || ''}</span>}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 14.5, color: theme.navy }}>{p.patientName}</div>
                      <div style={{ fontSize: 12.5, color: theme.textMuted, marginTop: 2 }}>
                        <strong>{p.insurerName}</strong>{p.tpaName ? ` (TPA: ${p.tpaName})` : ''} · #{p.policyNumber}
                      </div>
                      <div style={{ fontSize: 11.5, color: theme.textMuted, marginTop: 3, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {p.validTo && <span><Calendar size={11} style={{ verticalAlign: -1, marginRight: 3 }} />Valid till {fmt(p.validTo)}</span>}
                        {p.sumInsured > 0 && <span>Sum ₹{p.sumInsured.toLocaleString('en-IN')} ({utilPct}% used)</span>}
                        {p.copayPct > 0 && <span>Copay {p.copayPct}%</span>}
                        {p.preAuthRequired && <span style={{ color: '#f59e0b' }}>Pre-auth required &gt; ₹{p.preAuthThreshold}</span>}
                      </div>
                      {p.lastVerifiedAt && <div style={{ fontSize: 11, color: '#16a34a', marginTop: 3 }}>✓ Verified {fmt(p.lastVerifiedAt)} by {p.lastVerifiedBy}</div>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <button onClick={() => setVerifying(p)} style={{ ...btnSm, background: '#16a34a18', color: '#166534', borderColor: '#16a34a44' }}>
                        <CheckCircle2 size={11} /> Verify
                      </button>
                      <button onClick={() => { setEditing(p); setShowForm(true); }} style={btnSm}><Edit3 size={11} /> Edit</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      }

      {showForm && <PolicyForm API_BASE={API_BASE} policy={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); load(); showMsg('success', 'Saved'); }} />}
      {verifying && <VerifyModal policy={verifying} onClose={() => setVerifying(null)} onVerify={verify} />}
    </div>
  );
};

const PolicyForm = ({ API_BASE, policy, onClose, onSaved }) => {
  const [patients, setPatients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [f, setF] = useState(policy || {
    patientId: '', insurerName: '', policyNumber: '', planName: '', memberName: '',
    cardNumber: '', validFrom: '', validTo: '', sumInsured: 0, sumUtilized: 0, copayPct: 0,
    preAuthRequired: true, preAuthThreshold: 5000, isCashless: false, networkType: 'reimbursement',
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API_BASE}/patients`).then(r => setPatients(r.data || []));
    axios.get(`${API_BASE}/insurance/companies`).then(r => setCompanies(r.data || [])).catch(() => {});
  }, [API_BASE]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...f };
      if (body.validFrom) body.validFrom = new Date(body.validFrom).toISOString();
      if (body.validTo) body.validTo = new Date(body.validTo).toISOString();
      if (policy?._id) await axios.put(`${API_BASE}/insurance-policies/${policy._id}`, body);
      else await axios.post(`${API_BASE}/insurance-policies`, body);
      onSaved();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.error || e.message)); }
    setSaving(false);
  };

  return (
    <div style={modalBg} onClick={onClose}>
      <form onSubmit={submit} style={modal} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>{policy ? 'Edit' : 'New'} Insurance Policy</h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          <Field label="Patient *">
            <select required value={f.patientId} onChange={e => setF({ ...f, patientId: e.target.value })} style={inputStyle}>
              <option value="">Select…</option>
              {patients.map(p => <option key={p._id} value={p._id}>{p.name} ({p.uhid || p.phone})</option>)}
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Insurer *"><input required value={f.insurerName} onChange={e => setF({ ...f, insurerName: e.target.value })} style={inputStyle} placeholder="e.g. Star Health" /></Field>
            <Field label="TPA"><input value={f.tpaName || ''} onChange={e => setF({ ...f, tpaName: e.target.value })} style={inputStyle} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Policy Number *"><input required value={f.policyNumber} onChange={e => setF({ ...f, policyNumber: e.target.value })} style={inputStyle} /></Field>
            <Field label="Plan Name"><input value={f.planName || ''} onChange={e => setF({ ...f, planName: e.target.value })} style={inputStyle} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Card Number"><input value={f.cardNumber || ''} onChange={e => setF({ ...f, cardNumber: e.target.value })} style={inputStyle} /></Field>
            <Field label="Member Name"><input value={f.memberName || ''} onChange={e => setF({ ...f, memberName: e.target.value })} style={inputStyle} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Valid From"><input type="date" value={f.validFrom?.slice(0,10) || ''} onChange={e => setF({ ...f, validFrom: e.target.value })} style={inputStyle} /></Field>
            <Field label="Valid To"><input type="date" value={f.validTo?.slice(0,10) || ''} onChange={e => setF({ ...f, validTo: e.target.value })} style={inputStyle} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <Field label="Sum Insured ₹"><input type="number" value={f.sumInsured || 0} onChange={e => setF({ ...f, sumInsured: Number(e.target.value) })} style={inputStyle} /></Field>
            <Field label="Used ₹"><input type="number" value={f.sumUtilized || 0} onChange={e => setF({ ...f, sumUtilized: Number(e.target.value) })} style={inputStyle} /></Field>
            <Field label="Copay %"><input type="number" value={f.copayPct || 0} onChange={e => setF({ ...f, copayPct: Number(e.target.value) })} style={inputStyle} /></Field>
          </div>
          <div style={{ display: 'flex', gap: 14, fontSize: 13 }}>
            <label><input type="checkbox" checked={f.preAuthRequired} onChange={e => setF({ ...f, preAuthRequired: e.target.checked })} /> Pre-auth required</label>
            <label><input type="checkbox" checked={f.isCashless} onChange={e => setF({ ...f, isCashless: e.target.checked })} /> Cashless</label>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button type="button" onClick={onClose} style={{ ...btnSm }}>Cancel</button>
          <button type="submit" disabled={saving} style={{ ...btnStyle, marginTop: 0 }}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
};

const VerifyModal = ({ policy, onClose, onVerify }) => {
  const [status, setStatus] = useState('verified');
  const [notes, setNotes] = useState('');
  return (
    <div style={modalBg} onClick={onClose}>
      <div style={{ ...modal, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 10px' }}>Record Verification</h3>
        <div style={{ fontSize: 13, color: theme.textMuted, marginBottom: 12 }}>{policy.patientName} · {policy.insurerName} · #{policy.policyNumber}</div>

        <Field label="Status">
          <select value={status} onChange={e => setStatus(e.target.value)} style={inputStyle}>
            <option value="verified">✓ Verified — coverage confirmed</option>
            <option value="declined">✗ Declined — not eligible</option>
            <option value="expired">⚠ Expired — patient must renew</option>
          </select>
        </Field>
        <Field label="Notes (insurer remarks)"><textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} style={{ ...inputStyle, fontFamily: 'inherit' }} /></Field>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button onClick={onClose} style={btnSm}>Cancel</button>
          <button onClick={() => onVerify(policy._id, status, notes)} style={{ ...btnStyle, marginTop: 0 }}>Save Verification</button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: theme.textMuted, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</label>
    {children}
  </div>
);

const btnSm = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 11px', borderRadius: 6, border: `1px solid ${theme.border}`, background: 'white', color: theme.navy, cursor: 'pointer', fontSize: 12, fontWeight: 600 };
const modalBg = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modal = { background: 'white', borderRadius: 12, padding: 20, width: '100%', maxWidth: 540, maxHeight: '92vh', overflowY: 'auto' };

export default InsuranceVerifyPage;
